import { memo, useCallback, useEffect, useState } from 'react'
import styles from '../positions/index.module.scss'
import { Button, Modal } from 'antd'
import { LeagueFormat } from 'types/league-formats'
import {
    createLeagueFormat,
    deleteLeagueFormat,
    editLeagueFormat,
    getLeagueFormatById,
    getLeagueFormats,
} from 'services/league-formats'
import { LeagueFormatsTable } from '../../components/league-formats-table'
import { LeagueFormatForm } from '../../components/league-format-form'

const LeagueFormats = memo(() => {
    const [formats, setFormats] = useState<LeagueFormat[]>([])
    const [open, setOpen] = useState<boolean>(false)
    const [format, setFormat] = useState<LeagueFormat>({} as LeagueFormat)
    const [loading, setLoading] = useState<boolean>(false)
    const [dataIsFetching, setDataIsFetching] = useState<boolean>(false)

    const getAllData = useCallback(() => {
        setDataIsFetching(true)

        const getAll = async function () {
            return await getLeagueFormats()
        }

        getAll()
            .then(data => {
                setFormats(data)
            })
            .finally(() => {
                setDataIsFetching(false)
            })
    }, [])

    useEffect(() => {
        getAllData()
    }, [getAllData])

    const toggleModal = useCallback(() => {
        setOpen(!open)
    }, [open])

    const handleAdd = useCallback(() => {
        setFormat({} as LeagueFormat)
        toggleModal()
    }, [toggleModal])

    const handleEdit = useCallback(
        (id: number) => {
            setLoading(true)
            getLeagueFormatById(id)
                .then(item => {
                    setFormat(item)
                    toggleModal()
                })
                .finally(() => {
                    setLoading(false)
                })
        },
        [toggleModal]
    )

    const handleDelete = useCallback(
        (id: number) => {
            setLoading(true)
            deleteLeagueFormat(id)
                .then(getAllData)
                .finally(() => {
                    setLoading(false)
                })
        },
        [getAllData]
    )

    const handleFormSubmit = useCallback(
        (submitValues: LeagueFormat) => {
            setLoading(true)

            const refetchDataAndCloseModal = () => {
                getAllData()
                toggleModal()
            }

            if (format.id) {
                editLeagueFormat({
                    ...submitValues,
                    id: format.id,
                })
                    .then(refetchDataAndCloseModal)
                    .finally(() => {
                        setLoading(false)
                    })
            } else {
                createLeagueFormat(submitValues)
                    .then(refetchDataAndCloseModal)
                    .finally(() => {
                        setLoading(false)
                    })
            }
        },
        [getAllData, format, toggleModal]
    )

    const editMode = !!format.id
    const modalTitle = `${editMode ? `Edit league format ${format.name}` : `Add new league format`}`

    const somethingLoading = loading || dataIsFetching

    return (
        <div>
            League Formats
            <div className={styles.actions}>
                <Button type={'primary'} onClick={handleAdd}>
                    Add
                </Button>
            </div>
            <LeagueFormatsTable data={formats} onEdit={handleEdit} onDelete={handleDelete} loading={somethingLoading} />
            <Modal
                title={modalTitle}
                centered
                open={open}
                width={1000}
                destroyOnClose
                footer={null}
                onCancel={toggleModal}
                maskClosable={false}
            >
                <LeagueFormatForm leagueFormat={format} onSubmit={handleFormSubmit} loading={somethingLoading} />
            </Modal>
        </div>
    )
})

export default LeagueFormats
